/* Casanvero — product page signatures and hotspot explorer.
   Four small behaviours, wired by data attributes so each section stays declarative:
   [data-scrub]   a tall section whose sticky stage is driven by scroll (--p 0..1, data-step)
   [data-choice]  a set of buttons that sets data-value on the section
   [data-range]   a slider that sets --v 0..1 and fills [data-out] readouts
   [data-spots]   an image with hotspots that open pop-up cards
   Section-specific extras hook in through `sigs[name]`. */
(() => {
  const doc = document;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  const sigs = {};
  let scrubs = [];

  function init() {
  /* ---------- scrub ---------- */
  scrubs = [...doc.querySelectorAll('[data-scrub]')].map((el) => ({
    el,
    steps: +el.getAttribute('data-steps') || 0,
    items: [...el.querySelectorAll('[data-step-item]')],
    last: -1,
  }));
  // clicking a step jumps the page to that point of the scrub
  scrubs.forEach((s) => s.items.forEach((it, i) => {
    const btn = it.querySelector('button') || it;
    btn.addEventListener('click', () => {
      const run = s.el.offsetHeight - window.innerHeight;
      const top = s.el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: top + run * ((i + 0.5) / s.steps), behavior: reduce ? 'auto' : 'smooth' });
    });
  }));


  /* ---------- in-view: loops only run while their section is on screen ---------- */
  const vio = new IntersectionObserver((es) => es.forEach((e) => {
    e.target.classList.toggle('is-live', e.isIntersecting);
    // the launcher demonstrates itself once, the first time it's properly in view
    if (e.isIntersecting && e.intersectionRatio > 0.4 && !e.target.__played && e.target.getAttribute('data-sig') === 'launcher') {
      e.target.__played = true;
      setTimeout(() => launch(e.target), 500);
    }
  }), { rootMargin: '10% 0px', threshold: [0, 0.45] });
  doc.querySelectorAll('.cv-sig, .cv-spots').forEach((el) => vio.observe(el));

  /* ---------- choice ---------- */
  doc.querySelectorAll('[data-choice]').forEach((root) => {
    const btns = [...root.querySelectorAll('[data-choice-btn]')];
    const set = (v, user) => {
      root.setAttribute('data-value', v);
      btns.forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-choice-btn') === v)));
      const sig = sigs[root.getAttribute('data-sig')];
      if (sig && sig.choice) sig.choice(root, v, user);
    };
    btns.forEach((b) => b.addEventListener('click', () => set(b.getAttribute('data-choice-btn'), true)));
    set(root.getAttribute('data-value') || btns[0]?.getAttribute('data-choice-btn'), false);
  });

  /* ---------- range ---------- */
  doc.querySelectorAll('[data-range]').forEach((input) => {
    const root = input.closest('[data-sig]');
    const upd = () => {
      const min = +input.min, max = +input.max, v = +input.value;
      root.style.setProperty('--v', ((v - min) / (max - min)).toFixed(4));
      input.style.setProperty('--fill', ((v - min) / (max - min) * 100).toFixed(1) + '%');
      const sig = sigs[root.getAttribute('data-sig')];
      if (sig && sig.range) sig.range(root, v);
    };
    input.addEventListener('input', upd);
    upd();
  });

  /* ---------- hotspots ---------- */
  doc.querySelectorAll('[data-spots]').forEach((root) => {
    const spots = [...root.querySelectorAll('[data-spot]')];
    const idx = [...root.querySelectorAll('[data-spot-open]')];
    const close = () => { spots.forEach((s) => { s.classList.remove('is-open'); s.querySelector('button').setAttribute('aria-expanded', 'false'); }); idx.forEach((b) => b.classList.remove('is-open')); };
    const open = (s) => { close(); s.classList.add('is-open'); s.querySelector('button').setAttribute('aria-expanded', 'true'); const b = idx[spots.indexOf(s)]; if (b) b.classList.add('is-open'); };
    idx.forEach((b) => b.addEventListener('click', (e) => { e.stopPropagation(); root.__touched = true; open(spots[+b.getAttribute('data-spot-open')]); }));
    // on phones the card is a fixed sheet, so close it once the explorer scrolls away
    new IntersectionObserver((es) => { if (!es[0].isIntersecting) close(); }).observe(root.querySelector('.cv-spots-stage') || root);
    spots.forEach((s) => s.querySelector('button').addEventListener('click', (e) => {
      e.stopPropagation();
      root.__touched = true;
      s.classList.contains('is-open') ? close() : open(s);
    }));
    root.querySelectorAll('[data-spot-go]').forEach((b) => b.addEventListener('click', (e) => {
      e.stopPropagation();
      root.__touched = true;
      const i = spots.findIndex((s) => s.classList.contains('is-open'));
      open(spots[(i + (+b.getAttribute('data-spot-go')) + spots.length) % spots.length]);
    }));
    doc.addEventListener('click', (e) => { if (!root.contains(e.target)) close(); });
    doc.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    // open the first spot once, the first time the explorer is seen, so people know it's interactive
    const io = new IntersectionObserver((es) => {
      if (!es[0].isIntersecting) return;
      io.disconnect();
      // on phones the card is a bottom sheet, so only hint automatically on larger screens
      if (window.matchMedia('(min-width: 701px)').matches) setTimeout(() => { if (!root.__touched) open(spots[0]); }, 900);
    }, { threshold: 0.5 });
    if (spots.length) io.observe(root);
  });
  }

  function updateScrub(s) {
    const r = s.el.getBoundingClientRect();
    const run = s.el.offsetHeight - window.innerHeight;
    if (r.bottom < -50 || r.top > window.innerHeight + 50) return;
    const p = clamp(-r.top / (run || 1), 0, 1);
    s.el.style.setProperty('--p', p.toFixed(4));
    if (s.steps) {
      const step = Math.min(s.steps - 1, Math.floor(p * s.steps * 0.9999));
      s.el.style.setProperty('--sp', (p * s.steps - step).toFixed(4));
      if (step !== s.last) {
        s.el.setAttribute('data-step', step);
        s.items.forEach((it, i) => it.classList.toggle('is-active', i === step));
        s.last = step;
      }
    }
    const sig = sigs[s.el.getAttribute('data-sig')];
    if (sig && sig.scrub) sig.scrub(s.el, p);
  }

  let ticking = false;
  const onScroll = () => { ticking = false; scrubs.forEach(updateScrub); };
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });
  window.addEventListener('resize', onScroll);

  /* ======================================================================
     Per-product extras
     ====================================================================== */
  const fmtTime = (m) => String(Math.floor(m / 60) % 24).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');

  /* Vantage Feeder: meal plans placed on a 24-hour ring, plus a "feed now" drop */
  sigs.feeder = {
    choice(root, v) {
      const plans = JSON.parse(root.getAttribute('data-plans'));
      const times = plans[v] || [];
      root.querySelectorAll('[data-meal]').forEach((m, i) => {
        const on = i < times.length;
        m.classList.toggle('is-on', on);
        if (on) m.style.setProperty('--a', (times[i] * 0.25) + 'deg');
      });
      root.querySelectorAll('[data-meal-row]').forEach((r, i) => {
        const on = i < times.length;
        r.hidden = !on;
        if (on) r.querySelector('[data-meal-time]').textContent = fmtTime(times[i]);
      });
      const share = root.querySelector('[data-share]');
      if (share) share.textContent = times.length ? '1/' + times.length : '';
      root.style.setProperty('--meals', times.length);
    },
  };
  doc.querySelectorAll('[data-feed-now]').forEach((b) => b.addEventListener('click', () => {
    const root = b.closest('[data-sig]');
    root.classList.remove('is-feeding'); void root.offsetWidth; root.classList.add('is-feeding');
    clearTimeout(root.__ft); root.__ft = setTimeout(() => root.classList.remove('is-feeding'), 2600);
  }));

  /* Fetch Launcher: the ball's flight path is rebuilt for each distance */
  sigs.launcher = {
    choice(root, v, user) {
      const d = { 3: 239, 6: 383, 9: 527 }[v] || 527;          // landing x: mouth at 95, 48 units a metre
      const cy = { 3: 130, 6: 40, 9: -40 }[v] ?? -40;          // control point: higher for longer throws
      const path = `M 95 218 Q ${(95 + d) / 2} ${cy} ${d} 283`;
      root.querySelector('[data-arc]').setAttribute('d', path);
      root.querySelector('[data-ball]').style.offsetPath = `path("${path}")`;
      root.querySelector('[data-landing]').setAttribute('transform', `translate(${d} 0)`);
      if (user) launch(root);
    },
  };
  const launch = (root) => {
    root.classList.remove('is-launching'); void root.offsetWidth; root.classList.add('is-launching');
  };
  doc.querySelectorAll('[data-launch]').forEach((b) => b.addEventListener('click', () => launch(b.closest('[data-sig]'))));

  /* Pour Bowl: pour size → fill level and pours per 3 L reservoir */
  sigs.pour = {
    range(root, v) {
      root.querySelectorAll('[data-out="g"]').forEach((o) => (o.textContent = v));
      const cap = +root.getAttribute('data-capacity') || 3000;
      root.querySelectorAll('[data-out="pours"]').forEach((o) => (o.textContent = Math.floor(cap / v)));
    },
  };
  doc.querySelectorAll('[data-pour]').forEach((b) => b.addEventListener('click', () => {
    const root = b.closest('[data-sig]');
    root.classList.remove('is-pouring'); void root.offsetWidth; root.classList.add('is-pouring');
  }));

  /* Voyager Carrier: sizes to scale, readouts in cm and inches */
  sigs.carrier = {
    choice(root, v) {
      const sizes = JSON.parse(root.getAttribute('data-sizes'));
      const s = sizes[v]; if (!s) return;
      const max = sizes[Object.keys(sizes).pop()];
      root.style.setProperty('--sx', (s[0] / max[0]).toFixed(4));
      root.style.setProperty('--sy', (s[2] / max[2]).toFixed(4));
      root.style.setProperty('--sz', (s[1] / max[1]).toFixed(4));
      const inch = (cm) => (cm / 2.54).toFixed(1).replace(/\.0$/, '');
      ['l', 'w', 'h'].forEach((k, i) => root.querySelectorAll(`[data-dim="${k}"]`).forEach((o) => (o.textContent = `${s[i]} cm · ${inch(s[i])} in`)));
    },
  };

  /* Reservoir Fountain: a day counter across the week */
  sigs.reservoir = {
    scrub(root, p) {
      const day = Math.min(7, 1 + Math.floor(p * 7));
      if (root.__day === day) return;
      root.__day = day;
      root.querySelectorAll('[data-day]').forEach((o) => (o.textContent = 'Day ' + day));
      root.querySelectorAll('[data-day-dot]').forEach((d, i) => d.classList.toggle('is-on', i < day));
    },
  };


  /* GPS fence: a log-scale radius from 15 m to 2,000 m */
  sigs.fence = {
    range(root, v) {
      const r = 15 * Math.pow(2000 / 15, v / 100);
      const px = 30 + v * 1.6;
      root.style.setProperty('--R', px.toFixed(1));
      root.querySelectorAll('[data-fence-zone], [data-fence-ring]').forEach((c) => c.setAttribute('r', px.toFixed(1)));
      root.querySelectorAll('[data-fence-warn]').forEach((c) => c.setAttribute('r', (px * 0.85).toFixed(1)));
      const acres = Math.PI * r * r / 4046.86;
      const set = (k, t) => root.querySelectorAll('[data-out="' + k + '"]').forEach((o) => (o.textContent = t));
      set('m', Math.round(r).toLocaleString('en-US'));
      set('ft', Math.round(r * 3.281).toLocaleString('en-US'));
      set('acres', acres < 1 ? acres.toFixed(2) : acres < 100 ? acres.toFixed(1) : Math.round(acres).toLocaleString('en-US'));
    },
  };

  /* Cat wheel: four turns across the first two steps, then the lock holds it (1,000 mm track = 3.14 m a turn) */
  sigs.wheel = {
    scrub(root, p) {
      const turns = Math.min(p, 2 / 3) * 6;
      root.style.setProperty('--wh', (-turns * 360).toFixed(1) + 'deg');
      root.querySelectorAll('[data-wheel-m]').forEach((o) => (o.textContent = (turns * Math.PI).toFixed(1)));
      root.querySelectorAll('[data-wheel-turns]').forEach((o) => (o.textContent = turns.toFixed(1)));
    },
  };

  /* Cat tree: ceiling height in inches */
  sigs.tree = {
    range(root, v) {
      const set = (k, t) => root.querySelectorAll('[data-out="' + k + '"]').forEach((o) => (o.textContent = t));
      set('ftin', Math.floor(v / 12) + ' ft ' + (v % 12) + ' in');
      set('cm', Math.round(v * 2.54));
      set('ext', v - 90);
    },
  };

  /* Car seat: a quick brake to show the tether */
  doc.querySelectorAll('[data-brake]').forEach((b) => b.addEventListener('click', () => {
    const root = b.closest('[data-sig]');
    root.classList.remove('is-braking'); void root.offsetWidth; root.classList.add('is-braking');
    clearTimeout(root.__bt); root.__bt = setTimeout(() => root.classList.remove('is-braking'), 1800);
  }));

  /* Portal Cat Door, Groom Vacuum, Elevate Feeder: pure CSS off data-value, nothing extra needed */

  init();
  onScroll();
})();
